<template>
  <v-container fluid>
    <!-- Header Stats -->
    <v-row>
      <v-col cols="12" sm="6" md="3">
        <v-card class="mx-auto" color="primary" dark>
          <v-card-text>
            <div class="text-h6">Mensajes Enviados</div>
            <div class="text-h4 mt-2">
              {{ messageStats.total.toLocaleString() }}
              <v-icon small class="ml-2" v-if="messageStats.trend > 0">mdi-trending-up</v-icon>
              <v-icon small class="ml-2" v-if="messageStats.trend < 0">mdi-trending-down</v-icon>
            </div>
            <div class="text-caption">
              {{ messageStats.trend > 0 ? '+' : '' }}{{ messageStats.trend }}% vs mes anterior
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="mx-auto" color="success" dark>
          <v-card-text>
            <div class="text-h6">Tasa de Entrega</div>
            <div class="text-h4 mt-2">
              {{ deliveryRate }}%
              <v-icon small class="ml-2">mdi-check-circle</v-icon>
            </div>
            <div class="text-caption">
              {{ totalDelivered.toLocaleString() }} mensajes entregados
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="mx-auto" color="info" dark>
          <v-card-text>
            <div class="text-h6">Clientes Activos</div>
            <div class="text-h4 mt-2">
              {{ activeClients.toLocaleString() }}
              <v-icon small class="ml-2">mdi-account-group</v-icon>
            </div>
            <div class="text-caption">
              +{{ newClients }} nuevos este mes
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="mx-auto" color="warning" dark>
          <v-card-text>
            <div class="text-h6">Campañas Activas</div>
            <div class="text-h4 mt-2">
              {{ activeCampaigns }}
              <v-icon small class="ml-2">mdi-bullhorn</v-icon>
            </div>
            <div class="text-caption">
              {{ scheduledCampaigns }} programadas
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Charts Row -->
    <v-row class="mt-4">
      <!-- Messages Over Time Chart -->
      <v-col cols="12" md="8">
        <v-card>
          <v-card-title>
            Mensajes por Día
            <v-spacer></v-spacer>
            <v-btn-toggle v-model="timeRange" mandatory>
              <v-btn v-for="option in timeRangeOptions" :key="option.value" :value="option.value" small>
                {{ option.text }}
              </v-btn>
            </v-btn-toggle>
          </v-card-title>
          <v-card-text>
            <div ref="messageChart" :id="chartIds.message" style="height: 300px;"></div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Message Status Distribution -->
      <v-col cols="12" md="4">
        <v-card>
          <v-card-title>Estado de Mensajes</v-card-title>
          <v-card-text>
            <div ref="statusChart" :id="chartIds.status" style="height: 300px;"></div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Bottom Row -->
    <v-row class="mt-4">
      <v-col cols="12" md="6">
        <v-card>
          <v-card-title>Campañas Top</v-card-title>
          <v-card-text>
            <v-data-table :headers="campaignHeaders" :items="topCampaigns" :items-per-page="5" hide-default-footer
              dense>
              <template v-slot:item.deliveryRate="{ item }">
                <v-progress-linear :value="item.deliveryRate" height="20"
                  :color="getDeliveryRateColor(item.deliveryRate)">
                  <template v-slot:default="{ value }">
                    <strong>{{ value }}%</strong>
                  </template>
                </v-progress-linear>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card>
          <v-card-title>Horarios Pico</v-card-title>
          <v-card-text>
            <div ref="peakHoursChart" :id="chartIds.peakHours" style="height: 300px;"></div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import * as am5percent from "@amcharts/amcharts5/percent";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { getFirestore, doc, getDoc, collection } from "firebase/firestore";
import { firebaseApp } from "../firebase";
import axios from "axios";
import { buildApiUrl, getAuthHeaders } from "../config/api";

const db = getFirestore(firebaseApp);
const branchesRef = collection(db, "branches");

export default {
  name: 'Dashboard',

  data: () => ({
    messageStats: {
      total: 0,
      trend: 0,
      delivered: 0,
      failed: 0
    },
    deliveryRate: 0,
    totalDelivered: 0,
    activeClients: 0,
    newClients: 0,
    activeCampaigns: 0,
    scheduledCampaigns: 0,
    timeRange: 'week',
    timeRangeOptions: [
      { text: '7 días', value: 'week' },
      { text: '30 días', value: 'month' },
      { text: 'Todos', value: 'all' }
    ],
    campaignHeaders: [
      { text: 'Campaña', value: 'name' },
      { text: 'Destinatarios', value: 'recipients' },
      { text: 'Tasa de Entrega', value: 'deliveryRate' }
    ],
    topCampaigns: [],
    messageRoot: null,
    statusRoot: null,
    peakHoursRoot: null,
    currentBranch: null,
    statsInterval: null,
    chartIds: {
      message: 'message-chart',
      status: 'status-chart',
      peakHours: 'peak-hours-chart'
    }
  }),

  computed: {
    selectedBranch() {
      return this.$store.state.selectedBranch;
    }
  },

  watch: {
    selectedBranch: {
      handler(newValue) {
        this.currentBranch = newValue;
        this.loadDashboardData();
      },
      immediate: true
    },
    timeRange() {
      this.loadDashboardData();
    }
  },

  methods: {
    initMessageChart(messageData) {
      if (this.messageRoot) {
        this.messageRoot.dispose();
      }

      this.messageRoot = am5.Root.new(this.chartIds.message);
      this.messageRoot.setThemes([am5themes_Animated.new(this.messageRoot)]);

      const chart = this.messageRoot.container.children.push(
        am5xy.XYChart.new(this.messageRoot, {
          panX: false,
          panY: false
        })
      );

      const xAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(this.messageRoot, {
          categoryField: "date",
          renderer: am5xy.AxisRendererX.new(this.messageRoot, {})
        })
      );

      const yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(this.messageRoot, {
          renderer: am5xy.AxisRendererY.new(this.messageRoot, {})
        })
      );

      const series = chart.series.push(
        am5xy.LineSeries.new(this.messageRoot, {
          name: "Mensajes",
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: "value",
          categoryXField: "date",
          tooltip: am5.Tooltip.new(this.messageRoot, {
            labelText: "Mensajes: {valueY}"
          })
        })
      );

      xAxis.data.setAll(messageData);
      series.data.setAll(messageData);
    },

    initStatusChart(pieData) {
      if (this.statusRoot) {
        this.statusRoot.dispose();
      }

      this.statusRoot = am5.Root.new(this.chartIds.status);
      this.statusRoot.setThemes([am5themes_Animated.new(this.statusRoot)]);

      const chart = this.statusRoot.container.children.push(
        am5percent.PieChart.new(this.statusRoot, {
          layout: this.statusRoot.verticalLayout,
          innerRadius: am5.percent(50)
        })
      );

      const series = chart.series.push(
        am5percent.PieSeries.new(this.statusRoot, {
          valueField: "value",
          categoryField: "category",
          alignLabels: false
        })
      );

      series.labels.template.setAll({
        textType: "circular",
        centerX: 0,
        centerY: 0
      });

      series.data.setAll(pieData);
    },

    initPeakHoursChart(hourlyData) {
      if (this.peakHoursRoot) {
        this.peakHoursRoot.dispose();
      }

      this.peakHoursRoot = am5.Root.new(this.chartIds.peakHours);
      this.peakHoursRoot.setThemes([am5themes_Animated.new(this.peakHoursRoot)]);

      const chart = this.peakHoursRoot.container.children.push(
        am5xy.XYChart.new(this.peakHoursRoot, {
          panX: false,
          panY: false
        })
      );

      const formattedData = Array.from({ length: 24 }, (_, i) => ({
        hour: `${i.toString().padStart(2, '0')}:00`,
        value: hourlyData[i.toString().padStart(2, '0')] || 0
      }));

      const xAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(this.peakHoursRoot, {
          categoryField: "hour",
          renderer: am5xy.AxisRendererX.new(this.peakHoursRoot, {})
        })
      );

      const yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(this.peakHoursRoot, {
          renderer: am5xy.AxisRendererY.new(this.peakHoursRoot, {})
        })
      );

      const series = chart.series.push(
        am5xy.ColumnSeries.new(this.peakHoursRoot, {
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: "value",
          categoryXField: "hour",
          tooltip: am5.Tooltip.new(this.peakHoursRoot, {
            labelText: "Mensajes: {valueY}"
          })
        })
      );

      xAxis.data.setAll(formattedData);
      series.data.setAll(formattedData);
    },

    async loadDashboardData() {
      if (!this.currentBranch) return;

      try {
        const response = await axios.get(
          buildApiUrl('/api/stats/dashboard', {
            session: this.currentBranch,
            timeRange: this.timeRange
          }),
          {
            headers: getAuthHeaders(),
          }
        );

        const stats = response.data;

        // Actualizar estadísticas generales
        this.messageStats = {
          total: stats.generalStats.total_sent,
          delivered: stats.generalStats.delivered,
          failed: stats.generalStats.failed,
          trend: stats.generalStats.trend // Ahora el backend calcula la tendencia
        };

        this.deliveryRate = stats.generalStats.delivery_rate;
        this.totalDelivered = stats.generalStats.delivered;

        // Actualizar estadísticas de clientes (estadísticas simuladas)
        // En una implementación completa, estos valores vendrían del backend
        this.activeClients = stats.generalStats.total_sent > 0 ?
          Math.floor(stats.generalStats.total_sent / 4) : 0;
        this.newClients = Math.floor(this.activeClients * 0.15);

        // Actualizar estadísticas de campañas
        // El conteo de campañas activas y programadas puede ser simulado o
        // agregado al endpoint de stats en el futuro
        this.activeCampaigns = stats.campaignStats?.length || 0;
        this.scheduledCampaigns = Math.floor(this.activeCampaigns * 0.3);

        // Actualizar gráficos
        this.$nextTick(() => {
          // Gráfico de mensajes por día
          const messageData = stats.dailyStats.map(day => ({
            date: this.formatDate(day.date), // Formatear fecha para visualización
            value: day.total
          }));
          this.initMessageChart(messageData);

          // Gráfico de estado
          const pieData = [
            { category: "Entregados", value: stats.generalStats.delivered },
            { category: "Fallidos", value: stats.generalStats.failed }
          ];
          this.initStatusChart(pieData);

          // Gráfico de horas pico
          const hourlyData = {};
          stats.hourlyStats.forEach(hour => {
            hourlyData[hour.hour] = hour.total;
          });
          this.initPeakHoursChart(hourlyData);

          // Campañas top
          this.topCampaigns = stats.campaignStats.map(campaign => ({
            name: campaign.name,
            recipients: campaign.total_messages,
            deliveryRate: campaign.delivery_rate
          }));
        });

      } catch (error) {
        console.error('Error loading dashboard data:', error);
        // Mostrar mensaje de error al usuario
        this.$swal.fire({
          icon: 'error',
          title: 'Error al cargar datos',
          text: 'No se pudieron cargar las estadísticas. Intente nuevamente más tarde.',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000
        });
      }
    },

    // Método auxiliar para formatear fechas
    formatDate(dateStr) {
      // Convertir de formato ISO "2025-03-22" a formato legible "22/03"
      try {
        const date = new Date(dateStr);
        return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}`;
      } catch (e) {
        return dateStr; // En caso de error, devolver la cadena original
      }
    },

    getDeliveryRateColor(rate) {
      if (rate >= 98) return 'success';
      if (rate >= 95) return 'info';
      if (rate >= 90) return 'warning';
      return 'error';
    },

    async refreshStats() {
      try {
        await this.loadDashboardData();
      } catch (error) {
        console.error('Error refreshing stats:', error);
      }
    }
  },

  mounted() {
    this.$nextTick(async () => {
      try {
        await this.loadDashboardData();
        // Actualizar cada 5 minutos
        this.statsInterval = setInterval(this.refreshStats, 300000);
      } catch (error) {
        console.error('Error in mounted hook:', error);
      }
    });
  },

  beforeDestroy() {
    // Limpiar el intervalo
    if (this.statsInterval) {
      clearInterval(this.statsInterval);
      this.statsInterval = null;
    }

    // Destruir las instancias de los gráficos
    if (this.messageRoot) {
      this.messageRoot.dispose();
      this.messageRoot = null;
    }

    if (this.statusRoot) {
      this.statusRoot.dispose();
      this.statusRoot = null;
    }

    if (this.peakHoursRoot) {
      this.peakHoursRoot.dispose();
      this.peakHoursRoot = null;
    }
  }
};
</script>

<style scoped>
.v-card {
  transition: transform 0.2s;
}

.v-card:hover {
  transform: translateY(-2px);
}

/* Asegurar que los contenedores de los gráficos tengan un tamaño mínimo */
.v-card-text div[ref] {
  min-height: 300px;
  width: 100%;
}
</style>